// @ts-nocheck
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  Switch,
  FormControlLabel,
  Button,
  Icon,
  TextField,
} from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../../common/components';
import { useDialog, useOnChange, useOnMount } from '../../../../../../common/hooks';
import CompanyForm from '../../../common/CompanyForm';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '1200px',
    overflow: 'hidden',
  },
  searchButton: {
    marginLeft: theme.spacing(2),
  },
  searchField: {
    flex: '1',
    paddingRight: theme.spacing(2),
    '& .MuiInputBase-input': {
      height: '100%',
    },
  },
}));

function CompanyList(props) {
  const inputRef = useRef();
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState();
  const [selectionStart, setSelectionStart] = useState();

  const {
    data,
    filterParams,
    sortParams,
    onFetchData,
    onSelectCompany,
    onCreateCompany,
    onFilterChange,
    onSortChange,
    onCompanySearch,
    actionsDisabled,
  } = props;

  useOnMount(() => {
    if (filterParams.searchTerm) {
      setSearchTerm(filterParams.searchTerm);
    }
  });

  const [sortModel, setSortModel] = useState([
    {
      field: sortParams.orderBy,
      sort: sortParams.sortOrder,
    },
  ]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.selectionStart = selectionStart;
      inputRef.current.selectionEnd = selectionStart;
    }
  });

  useOnChange(sortModel, () => {
    if (_.isEmpty(sortModel)) {
      return;
    }

    onSortChange({
      orderBy: sortModel[0].field,
      sortOrder: sortModel[0].sort,
    });
  });

  const getColumnDefs = () => [
    {
      field: 'id',
      headerName: t('company.companyId'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('company.companyName'),
      width: 450,
      flex: 1,
    },
    {
      field: 'cvrNumber',
      headerName: t('company.companyCvrNumber'),
      width: 250,
      sortable: false,
      valueFormatter: params => params.value || '',
    },
    {
      field: 'eanNumber',
      headerName: t('company.companyEanNumber'),
      width: 250,
      sortable: false,
    },
    {
      field: 'status',
      headerName: t('company.companyStatus'),
      width: 250,
    },
  ];

  const getRowData = () => data.content;

  const handleSortModelChange = params => {
    if (!_.isEqual(params.sortModel, sortModel)) {
      setSortModel(params.sortModel);
    }
  };

  const handleFilterChange = event => {
    const filter = {
      ...filterParams,
    };

    if (event.target.id === 'CompanyList-showWithContracts') {
      filter.withContracts = event.target.checked;
    } else if (event.target.id === 'CompanyList-showActive') {
      filter.onlyActive = event.target.checked;
    }

    if (!filterParams.searchTerm) {
      onFilterChange(filter);
    } else {
      filter.searchTerm = filterParams.searchTerm;
      onCompanySearch(filter);
    }
  };

  const handleSearch = company => {
    setSelectionStart(inputRef.current.selectionStart);

    const params = {
      ...filterParams,
      searchTerm: company.trim(),
    };
    setSearchTerm(company);
    onCompanySearch(params);
  };

  const handleShowCreateCompanyDialog = () => {
    showDialog({
      title: t('company.companyCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <CompanyForm
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
          />
        </Box>
      ),
      onConfirm: onCreateCompany,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <TextField
        id="CompanySearchForm-term"
        name="company"
        label={t('company.search')}
        variant="filled"
        size="small"
        autoFocus
        inputRef={inputRef}
        className={classes.searchField}
        onChange={e => handleSearch(e.target.value)}
        value={searchTerm}
      />
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CompanyList-showActive"
            color="primary"
            checked={filterParams.onlyActive}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CompanyList-showWithContracts"
            color="primary"
            checked={filterParams.withContracts}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('company.companiesShowWithContracts')}
      />
      <Button
        id="CompanyList-createCompany"
        variant="outlined"
        color="primary"
        startIcon={<Icon>add_business</Icon>}
        onClick={handleShowCreateCompanyDialog}
        disabled={actionsDisabled}
      >
        {t('company.companyCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('company.companiesEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="CompanyList-container" className={classes.container}>
      <DataTable
        id="CompanyList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        rowCount={data.total}
        pageSize={data.size}
        pageIndex={data.page - 1}
        onRowClick={onSelectCompany}
        onPageChange={onFetchData}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        sortingMode="server"
        paginationMode="server"
        pagination
      />
    </Box>
  );
}

CompanyList.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    items: PropTypes.number,
    total: PropTypes.number,
    sort: PropTypes.shape({
      orderBy: PropTypes.string,
      sortOrder: PropTypes.string,
    }),
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  filterParams: PropTypes.shape({
    onlyActive: PropTypes.bool,
    withContracts: PropTypes.bool,
    searchTerm: PropTypes.string,
  }).isRequired,
  sortParams: PropTypes.shape({
    orderBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }).isRequired,
  onFetchData: PropTypes.func.isRequired,
  onSelectCompany: PropTypes.func.isRequired,
  onCreateCompany: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onCompanySearch: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
};

export default CompanyList;
